import firebase from "firebase/app";
import { useCallback, useEffect, useRef, useState } from "react";

import "firebase/auth";

/** SDK が読み込む grecaptcha のうち、ここで使う分だけ */
type Grecaptcha = { reset: (widgetId: number) => void };

const getGrecaptcha = (): Grecaptcha | undefined =>
  (window as Window & { grecaptcha?: Grecaptcha }).grecaptcha;

/**
 * 紐付けフローの状態。Firebase 側の状態機械をそのまま写している。
 * - input: ConfirmationResult をまだ持っていない
 * - codeSent: linkWithPhoneNumber が成功し ConfirmationResult を保持している
 * - completed: confirm() が成功し紐付けが完了した
 *
 * 送信先の番号は codeSent のときにしか存在しないため、状態に持たせて
 * 「codeSent なのに番号が無い」を型で作れないようにする。
 */
type LinkPhoneNumberState =
  | { step: "input" }
  | { step: "codeSent"; sentPhoneNumber: string }
  | { step: "completed" };

/**
 * SMSを送ったあと、コードを確かめるために持っておくもの。
 *
 * 新規の紐付けは linkWithPhoneNumber が ConfirmationResult を返すが、
 * 既に番号があるときは差し替えになり、verifyPhoneNumber が verificationId を
 * 返す。どちらか一方しか持たないので、まとめて1つで扱う。
 */
type PendingVerification =
  | { method: "link"; confirmationResult: firebase.auth.ConfirmationResult }
  | { method: "update"; verificationId: string };

export type LinkPhoneNumberError =
  | "invalidPhoneNumber"
  | "invalidCode"
  | "codeExpired"
  | "alreadyInUse"
  | "alreadyLinked"
  | "tooManyRequests"
  | "recaptchaFailed"
  | "requiresRecentLogin"
  | "unavailable"
  | "unknown";

const toLinkPhoneNumberError = (error: unknown): LinkPhoneNumberError => {
  const code =
    typeof error === "object" && error !== null && "code" in error
      ? String(error.code)
      : "";
  switch (code) {
    case "auth/invalid-phone-number":
    case "auth/missing-phone-number":
      return "invalidPhoneNumber";
    case "auth/invalid-verification-code":
    case "auth/missing-verification-code":
    case "auth/invalid-verification-id":
      return "invalidCode";
    // 番号の変更は直近のログインを求められる。再認証はこのフックの担当外
    case "auth/requires-recent-login":
      return "requiresRecentLogin";
    case "auth/code-expired":
      return "codeExpired";
    case "auth/credential-already-in-use":
    case "auth/account-exists-with-different-credential":
      return "alreadyInUse";
    // 他のアカウントではなく、ログイン中のアカウントに既に紐付いている場合。
    case "auth/provider-already-linked":
      return "alreadyLinked";
    case "auth/too-many-requests":
    case "auth/quota-exceeded":
      return "tooManyRequests";
    // Firebase プロジェクト側の設定が足りない場合。利用者が再試行しても直らない。
    // operation-not-allowed: Phone provider が無効
    // unauthorized-domain: アクセス元ドメインが承認済みドメインに無い
    case "auth/operation-not-allowed":
    case "auth/unauthorized-domain":
      return "unavailable";
    // reCAPTCHA のトークンが取れない・検証に失敗した場合。
    // 広告ブロッカーやトラッキング防止が www.google.com/recaptcha を遮断すると起きる。
    case "auth/invalid-app-credential":
    case "auth/captcha-check-failed":
      return "recaptchaFailed";
    default:
      return "unknown";
  }
};

/**
 * ログイン済み(メールアドレス認証)のアカウントに電話番号をSMS認証で紐付ける。
 * 電話番号単体でのログインは提供しないため、sign in ではなく linkWithPhoneNumber を使う。
 *
 * reCAPTCHA は Firebase Authentication の Phone provider が必須とするため、
 * recaptchaContainerId で指定した要素に invisible reCAPTCHA を描画する。
 * RecaptchaVerifier は1回の送信でしか使えないので、送信のたびに作り直す。
 */
export const useLinkPhoneNumber = (recaptchaContainerId: string) => {
  const [state, setState] = useState<LinkPhoneNumberState>({ step: "input" });
  const [error, setError] = useState<LinkPhoneNumberError | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // 再送の間隔を測るための、直近の送信先と時刻
  const [lastSent, setLastSent] = useState<{
    phoneNumber: string;
    at: number;
  } | null>(null);

  const verifierRef = useRef<firebase.auth.RecaptchaVerifier | null>(null);
  const pendingRef = useRef<PendingVerification | null>(null);

  /**
   * widget を破棄する。呼ぶのはアンマウントのときだけ。
   *
   * 送信のたびに破棄すると、次の送信で同じ要素へ2度目の描画になって
   * grecaptcha が失敗する。チャレンジの最中に描画先を消してしまうと
   * 突破した瞬間に grecaptcha が消えた要素へ触れて
   * "Cannot read properties of null (reading 'style')" になる。
   */
  const clearVerifier = useCallback(() => {
    try {
      verifierRef.current?.clear();
    } catch (e) {
      // 破棄済みの verifier に clear() すると throw することがある。
      // 後片付けなので処理を止めない。
      console.error(e);
    }
    verifierRef.current = null;
  }, []);

  /**
   * 送信に使う invisible reCAPTCHA を用意する。
   *
   * verifier は作り直さない。grecaptcha は描画先の要素を覚えていて、
   * 同じ要素へ2度目を描画すると
   * "reCAPTCHA has already been rendered in this element" で失敗する。
   * この例外は Firebase の error code を持たないため、原因の分からない
   * 失敗として表に出てしまう。
   *
   * 一方でトークンは1回しか使えないので、使い回すだけでは2回目の送信が
   * 通らない。widget を reset して新しいトークンを取れる状態に戻す。
   */
  const prepareVerifier = useCallback(async () => {
    if (verifierRef.current === null) {
      verifierRef.current = new firebase.auth.RecaptchaVerifier(
        recaptchaContainerId,
        { size: "invisible" }
      );
    }
    const verifier = verifierRef.current;
    // render() は描画済みなら同じ widget の id を返す
    const widgetId = await verifier.render();
    getGrecaptcha()?.reset(widgetId);
    return verifier;
  }, [recaptchaContainerId]);

  useEffect(() => {
    return clearVerifier;
  }, [clearVerifier]);

  const requestVerificationCode = useCallback(
    // 入力欄が妥当と判定した E.164 の番号を受け取る。検証は intl-tel-input が担う
    async (phoneNumber: string) => {
      setError(null);

      const { currentUser } = firebase.auth();
      if (currentUser === null) {
        setError("unknown");
        return;
      }

      setIsSubmitting(true);
      try {
        const verifier = await prepareVerifier();

        if (currentUser.phoneNumber === null) {
          pendingRef.current = {
            method: "link",
            confirmationResult: await currentUser.linkWithPhoneNumber(
              phoneNumber,
              verifier
            ),
          };
        } else {
          // 紐付け済みの番号を差し替える経路。link は provider-already-linked になる
          pendingRef.current = {
            method: "update",
            verificationId:
              await new firebase.auth.PhoneAuthProvider().verifyPhoneNumber(
                phoneNumber,
                verifier
              ),
          };
        }
        setLastSent({ phoneNumber, at: Date.now() });
        setState({ step: "codeSent", sentPhoneNumber: phoneNumber });
      } catch (e) {
        console.error(e);
        setError(toLinkPhoneNumberError(e));
      } finally {
        setIsSubmitting(false);
      }
    },
    [prepareVerifier]
  );

  const confirmVerificationCode = useCallback(async (code: string) => {
    setError(null);

    const pending = pendingRef.current;
    if (pending === null) {
      setError("unknown");
      return;
    }

    setIsSubmitting(true);
    try {
      if (pending.method === "link") {
        await pending.confirmationResult.confirm(code);
      } else {
        const { currentUser } = firebase.auth();
        if (currentUser === null) {
          setError("unknown");
          return;
        }
        const credential = firebase.auth.PhoneAuthProvider.credential(
          pending.verificationId,
          code
        );
        await currentUser.updatePhoneNumber(credential);
      }
      pendingRef.current = null;
      setState({ step: "completed" });
    } catch (e) {
      console.error(e);
      setError(toLinkPhoneNumberError(e));
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const backToInput = useCallback(() => {
    pendingRef.current = null;
    setError(null);
    setState({ step: "input" });
  }, []);

  return {
    state,
    error,
    isSubmitting,
    lastSent,
    requestVerificationCode,
    confirmVerificationCode,
    backToInput,
  };
};
